import React, { useEffect, useState } from "react";
import { ReworkBtn } from "@/components/erp/ReworkBtn";
import { api } from "@/lib/api";
import { Card, Badge, Btn, Modal, Field, Input, Select } from "@/components/erp/ui";
import { Play, Repeat, Settings2, Target, Lock } from "lucide-react";
import { toast } from "sonner";
import { statusMeta, fmtHM, refreshDesign } from "@/lib/designTimer";
import { DesignRounds } from "@/components/erp/DesignRounds";

export const LiveChip = ({ s }) => { const m = statusMeta(s.status); return (
  <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${m.soft}`} data-testid={`live-chip-${s.user_email}`}>
    <m.icon className="w-3 h-3" /> {s.user_name} · {m.label}
  </span>); };

const TargetBar = ({ w }) => {
  const used = w.total_seconds / 3600;
  const pct = w.target_hours ? Math.min(100, (used / w.target_hours) * 100) : 0;
  return (
    <div className="mt-3" data-testid={`design-wo-target-${w.code}`}>
      <div className="flex items-center text-[11px] text-slate-500 gap-1"><Target className="w-3 h-3" />
        {w.target_hours ? <>Target <b className="text-slate-700">{w.target_hours} jam</b>{w.target_source === "catalog" && " (katalog)"}{w.target_source === "estimate" && " (estimasi)"} · terpakai <b className={w.over_target ? "text-rose-600" : "text-slate-700"}>{fmtHM(w.total_seconds)}</b></> : <span className="text-amber-600">Target belum diisi</span>}
      </div>
      {w.target_hours > 0 && <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className={`h-full ${w.over_target ? "bg-rose-500" : pct > 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>}
    </div>
  );
};

export const WoCard = ({ w, me, active, canPlan, onStart, onPlan, onTakeover }) => {
  const late = w.past_deadline || (w.deadline && new Date(w.deadline) - Date.now() < 2 * 864e5);
  const locked = w.team.length > 0 && !w.mine && !canPlan;
  const credit = Object.entries(w.credit || {});
  return (
    <Card className={`p-4 ${w.express ? "bg-amber-50 border-amber-300" : ""} ${active ? "ring-2 ring-emerald-400" : ""}`} data-testid={`design-wo-${w.code}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-xs text-slate-500">{w.code} {w.express && <b className="text-amber-700">EXPRESS</b>} <span className="ml-1">{w.priority}</span></div>
          <div className="font-semibold truncate">{w.product}</div>
          <div className="text-xs text-slate-500 truncate">{w.customer_name || "—"}</div>
        </div>
        <Badge>{w.stage_status}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
        <div className="col-span-2"><div className="text-slate-400">Dikerjakan oleh</div>
          <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`design-wo-team-${w.code}`}>
            {w.team.length ? w.team.map((m) => <span key={m.email} className={`px-2 py-0.5 rounded-full ${m.email === me ? "bg-emerald-100 text-emerald-700" : "bg-slate-100"}`}>{m.name}{w.team.length > 1 ? ` · ${m.share}%` : ""}</span>) : <span className="text-slate-400">Belum dipegang — klik Kerjakan atau Take Over untuk menentukan pemegang</span>}
          </div>
        </div>
        <div><div className="text-slate-400">Deadline</div><b className={late ? "text-rose-600" : ""}>{w.deadline ? String(w.deadline).slice(0, 10) : "—"}</b></div>
        <div><div className="text-slate-400">Kerja hari ini</div><b data-testid={`design-wo-today-${w.code}`}>{fmtHM(w.today_seconds)}</b></div>
      </div>
      <TargetBar w={w} />
      <DesignRounds w={w} />
      {w.takeovers.length > 0 && <div className="mt-2 text-[11px] text-slate-500" data-testid={`design-wo-takeover-${w.code}`}>Take over terakhir: {w.takeovers[w.takeovers.length - 1].from_name} → <b>{w.takeovers[w.takeovers.length - 1].to_name}</b>{w.takeovers[w.takeovers.length - 1].full_credit ? " (poin penuh)" : ""}</div>}
      {credit.length > 0 && <div className="mt-1 text-[11px] text-slate-500" data-testid={`design-wo-credit-${w.code}`}>Poin KPI: {credit.map(([e, f]) => `${(w.team.find((m) => m.email === e) || w.live.find((s) => s.user_email === e) || {}).name || e.split("@")[0]} ${Math.round(f * 100)}%`).join(" · ")}</div>}
      <div className="flex flex-wrap gap-1 mt-2">{w.live.map((s) => <LiveChip key={s.id} s={s} />)}</div>
      <ReworkBtn woId={w.id} stage="DESIGN" />
      <div className="flex gap-2 mt-3">
        <Btn className="flex-1" data-testid={`design-start-${w.code}`} disabled={locked} onClick={() => onStart(w)} variant={active ? "ghost" : undefined}>
          {locked ? <><Lock className="w-4 h-4" /> Dipegang tim lain</> : <><Play className="w-4 h-4" /> {active ? "Sedang dikerjakan" : "Kerjakan WO ini"}</>}
        </Btn>
        <Btn variant="ghost" data-testid={`design-takeover-${w.code}`} onClick={() => onTakeover(w)} title="Take over"><Repeat className="w-4 h-4" /> Take Over</Btn>
        {canPlan && <Btn variant="ghost" data-testid={`design-plan-${w.code}`} onClick={() => onPlan(w)} title="Atur tim & target"><Settings2 className="w-4 h-4" /></Btn>}
      </div>
    </Card>
  );
};

export const PlanModal = ({ w, members, onClose }) => {
  const [target, setTarget] = useState("");
  const [orig, setOrig] = useState("");
  const [team, setTeam] = useState([]);
  useEffect(() => { if (w) { const t = w.target_hours || ""; setTarget(t); setOrig(t); setTeam(w.team.map((m) => ({ ...m }))); } }, [w]);
  const add = (email) => { const m = members.find((x) => x.email === email); if (m && !team.some((t) => t.email === email)) setTeam([...team, { email, name: m.name, share: 0 }]); };
  const even = () => { const b = Math.floor(10000 / team.length) / 100; setTeam(team.map((t, i) => ({ ...t, share: i === team.length - 1 ? Math.round((100 - b * (team.length - 1)) * 100) / 100 : b }))); };
  const sum = team.reduce((s, t) => s + (+t.share || 0), 0);
  const save = async () => {
    try { await api.put(`/design/wo/${w.id}/plan`, { ...(String(target) !== String(orig) && target !== "" ? { target_hours: +target } : {}), team: team.map((t) => ({ email: t.email, share: +t.share || 0 })) }); toast.success("Tim & target disimpan"); refreshDesign(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };
  return (
    <Modal open={!!w} onClose={onClose} title={`Atur Tim & Target · ${w?.code || ""}`} footer={<Btn data-testid="plan-save" onClick={save}>Simpan</Btn>}>
      <Field label="Target waktu design (jam)"><Input type="number" min="0" step="0.5" data-testid="plan-target" value={target} onChange={(e) => setTarget(e.target.value)} /></Field>
      <div className="mt-3 text-sm font-semibold">Tim pengerja & pembagian poin KPI</div>
      <div className="space-y-2 mt-2">
        {team.map((t) => (
          <div key={t.email} className="flex items-center gap-2" data-testid={`plan-member-${t.email}`}>
            <span className="flex-1 min-w-0 text-sm truncate">{t.name}</span>
            <div className="w-20 shrink-0"><Input type="number" className="text-right" data-testid={`plan-share-${t.email}`} value={t.share} onChange={(e) => setTeam(team.map((x) => x.email === t.email ? { ...x, share: e.target.value } : x))} /></div><span className="text-xs">%</span>
            <button type="button" className="text-xs text-rose-500" onClick={() => setTeam(team.filter((x) => x.email !== t.email))}>hapus</button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Select data-testid="plan-add-member" value="" onChange={(e) => add(e.target.value)}>
          <option value="">+ Tambah orang</option>
          {members.filter((m) => !team.some((t) => t.email === m.email)).map((m) => <option key={m.email} value={m.email}>{m.name} · {m.role}</option>)}
        </Select>
        {team.length > 1 && <Btn variant="ghost" data-testid="plan-even" onClick={even}>Bagi rata</Btn>}
        <span className={`ml-auto text-xs ${team.length && Math.abs(sum - 100) > 0.5 && sum > 0 ? "text-rose-600" : "text-slate-500"}`} data-testid="plan-sum">Total {Math.round(sum * 100) / 100}%</span>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Kosongkan semua persen (0) untuk dibagi rata otomatis.</p>
    </Modal>
  );
};

export const TakeoverModal = ({ w, me, members, reasons, canPlan, onClose }) => {
  const mine = w?.mine;
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (w) { setTo(mine ? "" : me); setFrom(mine ? me : (w.team[0] || {}).email || ""); setReason(""); setNote(""); } }, [w, me, mine]);
  const submit = async () => {
    try { await api.post(`/design/wo/${w.id}/takeover`, { to_email: to, from_email: from, reason, note }); toast.success("WO berhasil dipindahkan"); refreshDesign(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal take over"); }
  };
  return (
    <Modal open={!!w} onClose={onClose} title={`Take Over · ${w?.code || ""}`} footer={<Btn data-testid="takeover-submit" disabled={!to || !reason} onClick={submit}>{mine ? "Pindahkan" : "Ambil WO ini"}</Btn>}>
      {w && w.team.length > 1 && <Field label="Pemegang yang digantikan"><Select data-testid="takeover-from" value={from} onChange={(e) => setFrom(e.target.value)}>{w.team.map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}</Select></Field>}
      <Field label="Dipindahkan ke">
        <Select data-testid="takeover-to" value={to} disabled={!mine && !canPlan} onChange={(e) => setTo(e.target.value)}>
          <option value="">— pilih modeler —</option>
          {members.filter((m) => m.email !== from).map((m) => <option key={m.email} value={m.email}>{m.name} · {m.role}</option>)}
        </Select>
      </Field>
      <Field label="Alasan">
        <Select data-testid="takeover-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">— pilih alasan —</option>
          {Object.entries(reasons).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Select>
      </Field>
      <Field label="Keterangan"><Input data-testid="takeover-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="opsional, wajib untuk alasan lain" /></Field>
      <p className="text-[11px] text-slate-500 mt-2">{reason && reason !== "LAINNYA" ? "Poin KPI WO ini diambil penuh oleh pengerja terakhir." : reason === "LAINNYA" ? "Poin KPI dibagi sesuai jam kerja masing-masing." : "Absen / lewat target / lewat deadline / skill belum memadai = poin penuh ke pengambil."}</p>
    </Modal>
  );
};
